import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Editor, Node, Transforms, Range, createEditor } from 'slate';
import { ReactEditor, Editable, Slate, withReact } from 'slate-react';
import { defineMessages, useIntl } from 'react-intl';
import config from '@plone/volto/registry';
import { P } from '@plone/volto-slate/constants';
import { withBlockExtensions } from '@plone/volto/helpers';

const messages = defineMessages({
  placeholder: {
    id: 'mpf_section_heading_placeholder',
    defaultMessage: 'Título de seção…',
  },
  editable: {
    id: 'mpf_section_heading_editable',
    defaultMessage: 'Título de seção',
  },
});

// Mesma proteção usada na customização do bloco Title: `ReactEditor.focus`
// pode estourar se o foco rodar antes do nó estar montado no slate-react.
const safeFocus = (editor) => {
  try {
    ReactEditor.focus(editor);
    return true;
  } catch {
    return false;
  }
};

function usePrevious(value) {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

const Edit = (props) => {
  const {
    block,
    blockNode,
    data,
    detached,
    editable,
    index,
    onAddBlock,
    onChangeBlock,
    onFocusNextBlock,
    onFocusPreviousBlock,
    onSelectBlock,
    selected,
  } = props;

  const [editor] = useState(withReact(createEditor()));
  const [initialValue] = useState([
    { type: P, children: [{ text: data?.title || '' }] },
  ]);

  const intl = useIntl();

  const prevSelected = usePrevious(selected);

  const text = useMemo(() => data?.title || '', [data]);

  const placeholder = useMemo(
    () => intl.formatMessage(messages.placeholder),
    [intl],
  );
  const disableNewBlocks = useMemo(() => detached, [detached]);

  useEffect(() => {
    if (!prevSelected && selected) {
      if (editor.selection && Range.isCollapsed(editor.selection)) {
        safeFocus(editor);
      } else {
        setTimeout(() => {
          const focused = safeFocus(editor);
          if (!focused) {
            if (safeFocus(editor)) {
              Transforms.select(editor, Editor.end(editor, []));
            }
          }
        }, 0);
      }
    }
  }, [prevSelected, selected, editor]);

  useEffect(() => {
    const oldText = Node.string(editor);
    if (oldText !== text) {
      Transforms.insertText(editor, text, { at: [0, 0] });
    }
  }, [editor, text]);

  const handleChange = useCallback(() => {
    const newText = Node.string(editor);
    if (newText !== text) {
      onChangeBlock(block, { ...data, title: newText });
    }
  }, [editor, onChangeBlock, block, data, text]);

  const handleKeyDown = useCallback(
    (ev) => {
      if (ev.key === 'Return' || ev.key === 'Enter') {
        ev.preventDefault();
        if (!disableNewBlocks) {
          onSelectBlock(
            onAddBlock(config.settings.defaultBlockType, index + 1),
          );
        }
      } else if (ev.key === 'ArrowUp') {
        ev.preventDefault();
        onFocusPreviousBlock(block, blockNode.current);
      } else if (ev.key === 'ArrowDown') {
        ev.preventDefault();
        onFocusNextBlock(block, blockNode.current);
      }
    },
    [
      index,
      blockNode,
      disableNewBlocks,
      onSelectBlock,
      onAddBlock,
      onFocusPreviousBlock,
      onFocusNextBlock,
      block,
    ],
  );

  const handleFocus = useCallback(() => {
    onSelectBlock(block);
  }, [block, onSelectBlock]);

  const renderElement = useCallback(({ attributes, children }) => {
    return (
      <h2 {...attributes} className="mpf-section-heading__title">
        {children}
      </h2>
    );
  }, []);

  if (typeof window.__SERVER__ !== 'undefined') {
    // Mesma estrutura do retorno abaixo (header > [título, linha]), sem o
    // Slate por dentro — evita mismatch de hidratação: se o servidor manda
    // um <div/> e o cliente espera <header>, o React descarta o node e
    // remonta a subárvore inteira do zero.
    return (
      <header className="mpf-section-heading">
        <h2 className="mpf-section-heading__title">{text}</h2>
        <span aria-hidden="true" className="mpf-section-heading__line" />
      </header>
    );
  }

  return (
    <header className="mpf-section-heading">
      <Slate
        editor={editor}
        onChange={handleChange}
        initialValue={initialValue}
      >
        <Editable
          readOnly={!editable}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          renderElement={renderElement}
          onFocus={handleFocus}
          aria-multiline="false"
          aria-label={intl.formatMessage(messages.editable)}
        />
      </Slate>

      <span aria-hidden="true" className="mpf-section-heading__line" />
    </header>
  );
};

Edit.defaultProps = {
  detached: false,
  editable: true,
};

export default withBlockExtensions(Edit);
